/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { toTitleCase } from 'sarsaparilla';

import type { IconBadgeName } from '../IconBadge/IconBadge';
import type {
    LocationSuggestion,
    InventorySuggestion,
    SearchSuggestionItem,
    SearchSuggestionResult,
    SearchSuggestionGroup,
    SearchResult,
    SearchResultItem,
} from './search.types';

type ValidatedSearchResultItem = SearchResultItem & {
    latitude: string;
    longitude: string;
};

export function transformLocationSuggestions(
    suggestions?: LocationSuggestion[] | null
): SearchSuggestionItem[] {
    if (!suggestions) {
        return [];
    }

    return suggestions.map((suggestion) => {
        return {
            text: suggestion.text,
            icon: suggestion.type?.toLowerCase() === 'rec area' ? 'tree' : 'pin',
            lat: suggestion.lat,
            lng: suggestion.lng,
            lat_ne: suggestion.lat_ne,
            lng_ne: suggestion.lng_ne,
            lat_sw: suggestion.lat_sw,
            lng_sw: suggestion.lng_sw,
            location: suggestion.text,
            id: `${suggestion.lat}-${suggestion.lng}`,
            type: 'location',
        };
    });
}

export function transformInventorySuggestions(
    suggestions?: InventorySuggestion[] | null
): SearchSuggestionItem[] {
    if (!suggestions) {
        return [];
    }

    const filteredSuggestions = suggestions.filter((suggestion) => {
        return (
            suggestion.lat &&
            suggestion.lng &&
            suggestion.city &&
            suggestion.state_code &&
            suggestion.name
        );
    });

    return filteredSuggestions.map((suggestion) => {
        return {
            text: toTitleCase(suggestion.name),
            description: `Near ${suggestion.city}, ${suggestion.state_code}`,
            icon: getInventoryIcon(suggestion),
            lat: suggestion.lat,
            lng: suggestion.lng,
            location: toTitleCase(suggestion.name),
            id: suggestion.entity_id,
            type: 'inventory',
        };
    });
}

export function transformSearchSuggestionResult(
    result?: SearchSuggestionResult
): SearchSuggestionGroup[] {
    if (!result) {
        return [];
    }

    /**
     * Only include groups that have items. Passing empty groups to Combobox.Root
     * causes base-ui to skip the render prop for them while leaving the previous
     * group's DOM nodes in place, making stale suggestions appear above new ones.
     */
    const groups: SearchSuggestionGroup[] = [
        {
            groupType: 'locations',
            items: transformLocationSuggestions(result.suggestions),
        },
        {
            groupType: 'inventory',
            items: transformInventorySuggestions(result.inventory_suggestions),
        },
    ];

    return groups.filter((group) => group.items.length > 0);
}

export function transformSearchResult(
    data?: SearchResult,
    searchedId?: string | null
): SearchResult | undefined {
    const decodedLocation = data?.location
        ? decodeHtmlEntities(data.location)
        : undefined;

    if (!data?.results || !data.results.length) {
        return {
            ...data,
            location: decodedLocation,
            results: [],
        } as SearchResult;
    }

    const validItems = data.results.filter((item): item is ValidatedSearchResultItem => {
        return (
            Boolean(item.latitude) &&
            Boolean(item.longitude) &&
            item.latitude !== '0' &&
            item.longitude !== '0'
        );
    });

    let validatedItems = offsetDuplicateCoordinates(validItems);

    // If locationId is set and an item matches it, put that item first
    if (searchedId) {
        const matchIndex = validatedItems.findIndex(
            (item) => item.entity_id === searchedId
        );

        if (matchIndex > 0) {
            const [match] = validatedItems.splice(matchIndex, 1);
            validatedItems = [match, ...validatedItems];
        }
    }

    return {
        ...data,
        location: decodedLocation,
        results: validatedItems,
    };
}

/**
 * Offsets items that share identical coordinates so map markers don't overlap.
 * Duplicates are distributed in a circular pattern ~20 feet from the original.
 */
function offsetDuplicateCoordinates(
    items: Array<ValidatedSearchResultItem>
): Array<ValidatedSearchResultItem> {
    const OFFSET_FEET = 20;
    const FEET_PER_DEGREE_LAT = 364000;
    const offsetDegrees = OFFSET_FEET / FEET_PER_DEGREE_LAT;

    // Group items by coordinate key
    const coordGroups = new Map<string, Array<ValidatedSearchResultItem>>();
    items.forEach((item) => {
        const key = `${item.latitude},${item.longitude}`;
        const group = coordGroups.get(key) || [];
        group.push(item);
        coordGroups.set(key, group);
    });

    // Process groups and offset duplicates
    const result: Array<ValidatedSearchResultItem> = [];
    coordGroups.forEach((group) => {
        if (group.length === 1) {
            result.push(group[0]);
        } else {
            // First item stays at original position
            result.push(group[0]);
            // Offset remaining items in a circle
            for (let i = 1; i < group.length; i += 1) {
                const angle = ((2 * Math.PI) / (group.length - 1)) * (i - 1);
                const latOffset = offsetDegrees * Math.cos(angle);
                const lngOffset = offsetDegrees * Math.sin(angle);
                result.push({
                    ...group[i],
                    latitude: (parseFloat(group[i].latitude) + latOffset).toString(),
                    longitude: (parseFloat(group[i].longitude) + lngOffset).toString(),
                });
            }
        }
    });

    return result;
}

/**
 * Decodes common HTML entities in strings returned by the search API.
 * The backend may return entity-encoded text (e.g. "&amp;" for "&"); this
 * normalizes it for display and comparison.
 */
export function decodeHtmlEntities(str: string | null | undefined): string {
    if (str == null || str === '') {
        return '';
    }
    return str
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&');
}

export function getInventoryIcon(suggestion: InventorySuggestion): IconBadgeName {
    switch (suggestion.entity_type) {
        case 'campground': {
            if (
                suggestion?.campsite_type_of_use?.length === 1 &&
                suggestion?.campsite_type_of_use[0] === 'Day'
            ) {
                return 'picnic-table';
            }
            return 'tent';
        }

        case 'ticketfacility':
        case 'tour':
        case 'timedentry':
        case 'timedentry_tour':
            return 'ticket';

        case 'permit':
        case 'vehiclepermit':
            return 'card';

        case 'recarea':
            return 'tree';

        case 'pass':
            return 'card';

        case 'activitypass':
            return 'card';

        case 'treepermit':
            return 'card';

        case 'interagencypass':
            return 'card';

        case 'venuereservations':
        case 'venuereservations_venue':
            return 'picnic-table';

        default:
            return 'pin';
    }
}

// cSpell:ignore ticketfacility, timedentry, vehiclepermit, recarea, treepermit, activitypass, interagencypass, venuereservations
